import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, ArrowLeft, ExternalLink, Linkedin, Twitter } from 'lucide-react';
import { useDataService } from '../context/DataContext';
import useResponsive from '../hooks/useResponsive';
import LoadingSpinner from '../components/ui/LoadingSpinner';

const Contact = () => {
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { isMobile } = useResponsive();
  const dataService = useDataService();

  useEffect(() => {
    const loadProfile = async () => {
      try {
        setLoading(true);
        const profile = await dataService.getProfile();
        setProfileData(profile);
      } catch (err) {
        console.error('Error loading profile:', err);
        // Fallback to mock data
        try {
          const { profileData: mockProfile } = await import('../data/mock');
          setProfileData(mockProfile);
        } catch (mockErr) {
          console.error('Error loading mock profile:', mockErr);
        }
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [dataService]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Unable to load contact information</h2>
        <Link 
          to="/"
          className="text-gray-900 hover:text-gray-700 font-medium transition-colors"
        >
          Back
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Navigation */}
      <nav className="mb-8">
        <Link 
          to="/" 
          className="text-vintage-faded hover:text-vintage-accent flex items-center space-x-2 transition-colors"
        >
          <ArrowLeft size={16} />
            <span>Back</span>
        </Link>
      </nav>

      {/* Header */}
      <header className="mb-12 text-center">
        <h1 className="vintage-heading-1 text-4xl md:text-5xl text-vintage-ink mb-4">
          Get In Touch
        </h1>
        <p className="vintage-body text-vintage-faded max-w-2xl mx-auto">
          I'm always interested in discussing startup ecosystems, investment opportunities, 
          and market trends. Feel free to reach out if you'd like to collaborate or exchange insights.
        </p>
      </header>

      {/* Contact Information - Compact */}
      <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-12">
        {/* Email */}
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-vintage-accent rounded-lg flex items-center justify-center flex-shrink-0">
            <Mail className="text-vintage-cream" size={18} />
          </div>
          <div>
            <span className="vintage-caption text-vintage-faded block">Email</span>
            <a 
              href={`mailto:${profileData.email}`}
              className="vintage-body text-vintage-accent hover:text-vintage-ink transition-colors text-sm"
            >
              {profileData.email}
            </a>
          </div>
        </div>

        {/* Location */}
        {profileData.location && (
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-vintage-accent rounded-lg flex items-center justify-center flex-shrink-0">
              <MapPin className="text-vintage-cream" size={18} />
            </div>
            <div>
              <span className="vintage-caption text-vintage-faded block">Location</span>
              <p className="vintage-body text-vintage-ink text-sm">
                {profileData.location}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Social Links */}
      {profileData.social && (
        <section className="mb-12">
          <h2 className="vintage-heading-3 text-vintage-ink mb-6 text-center">Connect on Social Media</h2>
          <div className="flex flex-wrap justify-center gap-4">
            {profileData.social.linkedin && (
              <a 
                href={profileData.social.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="vintage-btn inline-flex items-center space-x-2"
              >
                <Linkedin size={18} />
                <span>LinkedIn</span>
                <ExternalLink size={14} />
              </a>
            )}
            {profileData.social.twitter && (
              <a 
                href={profileData.social.twitter}
                target="_blank"
                rel="noopener noreferrer"
                className="vintage-btn inline-flex items-center space-x-2"
              >
                <Twitter size={18} />
                <span>Twitter</span>
                <ExternalLink size={14} />
              </a>
            )}
            {profileData.social.github && (
              <a 
                href={profileData.social.github}
                target="_blank"
                rel="noopener noreferrer"
                className="vintage-btn inline-flex items-center space-x-2"
              >
                <ExternalLink size={18} />
                <span>GitHub</span>
                <ExternalLink size={14} />
              </a>
            )}
            {profileData.social.substack && (
              <a 
                href={profileData.social.substack}
                target="_blank"
                rel="noopener noreferrer"
                className="vintage-btn inline-flex items-center space-x-2"
              >
                <ExternalLink size={18} />
                <span>Substack</span>
                <ExternalLink size={14} />
              </a>
            )}
          </div>
        </section>
      )}

      {/* Quick Contact */}
      <section className="vintage-card p-8 text-center">
        <h2 className="vintage-heading-2 text-2xl text-vintage-ink mb-4">Send an Email</h2>
        <p className="vintage-body text-vintage-faded mb-6">
          The best way to reach me is via email. I typically respond within 24-48 hours.
        </p>
        <a 
          href={`mailto:${profileData.email}?subject=${encodeURIComponent('Contact from Portfolio')}`}
          className="vintage-btn vintage-btn-accent inline-flex items-center space-x-2"
          onClick={(e) => {
            // Try Gmail first, fallback to mailto
            const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(profileData.email)}&su=${encodeURIComponent('Contact from Portfolio')}`;
            window.open(gmailUrl, '_blank');
            e.preventDefault();
          }}
        >
          <Mail size={18} />
          <span>Send Email</span>
        </a>
      </section>
    </div>
  );
};

export default Contact;

